"use client";

import React from "react";
import { VisitListProps } from "./types";
import { defaultVisitListTheme } from "./themes";
import { mergeTheme } from "../utils/theme";
import { VisitListItem } from "./visit-list-item";

/**
 * VisitList Component
 *
 * A comprehensive component for displaying healthcare visits organized by current and past sections.
 * Handles loading states, error states, empty states, and provides customization options for theming.
 *
 * @param data - Object containing current and past visit arrays
 * @param isLoading - Whether the component is in a loading state
 * @param error - Error object if data fetching failed
 * @param onVisitClick - Callback when a visit item is clicked
 * @param onNewVisitClick - Callback when new visit button is clicked
 * @param showNewVisitButton - Whether to show the new visit button
 * @param theme - Theme overrides for styling
 * @param className - Additional CSS classes
 * @param emptyStateComponent - Custom empty state component
 * @param loadingComponent - Custom loading component
 * @param errorComponent - Custom error component
 * @param visitItemComponent - Custom visit item component
 * @param newVisitButtonComponent - Custom new visit button component
 * @param currentSectionTitle - Title for current visits section
 * @param pastSectionTitle - Title for past visits section
 */
export const VisitList: React.FC<VisitListProps> = ({
  data,
  isLoading = false,
  error = null,
  onVisitClick,
  onNewVisitClick,
  showNewVisitButton = true,
  theme,
  className = "",
  emptyStateComponent,
  loadingComponent,
  errorComponent,
  visitItemComponent: CustomVisitItem,
  newVisitButtonComponent,
  currentSectionTitle = "Current",
  pastSectionTitle = "Past",
}) => {
  const mergedTheme = mergeTheme(defaultVisitListTheme, theme);
  const VisitItemComponent = CustomVisitItem || VisitListItem;

  // Loading state
  if (isLoading) {
    if (loadingComponent) {
      return <>{loadingComponent}</>;
    }
    return (
      <div className={`${mergedTheme.container} ${className}`}>
        <div className="loading-skeleton">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-[120px] bg-gray-100 rounded-2xl mb-4 animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    if (errorComponent) {
      return <>{errorComponent}</>;
    }
    return (
      <div className={`${mergedTheme.container} ${className}`}>
        <div className="text-center py-8">
          <p className="text-red-500 mb-2">Error loading visits</p>
          <p className="text-gray-500 text-sm">
            {error.message || "Please try again later"}
          </p>
        </div>
      </div>
    );
  }

  // Empty state
  const hasCurrentVisits = data?.current && data.current.length > 0;
  const hasPastVisits = data?.past && data.past.length > 0;
  const isEmpty = !hasCurrentVisits && !hasPastVisits;

  if (isEmpty) {
    if (emptyStateComponent) {
      return <>{emptyStateComponent}</>;
    }
    return (
      <div className={`${mergedTheme.container} ${className}`}>
        <div className="text-center py-12 px-4">
          <h2 className={mergedTheme.header?.title}>No visits yet</h2>
          <p className={mergedTheme.header?.subtitle}>
            Start a new visit to connect with a healthcare provider
          </p>
          {showNewVisitButton && onNewVisitClick && (
            <div className="mt-6">{newVisitButtonComponent}</div>
          )}
        </div>
      </div>
    );
  }

  // Render visit sections
  const renderSection = (
    title: React.ReactNode,
    visits: typeof data.current,
    showNotification = false,
    showButtonAfter = false
  ) => {
    if (!visits || visits.length === 0) return null;

    return (
      <div className={mergedTheme.section?.container}>
        <h3 className={mergedTheme.section?.title}>{title}</h3>
        <div className="flex flex-col gap-2">
          {visits.map((visit, index) => (
            <VisitItemComponent
              key={visit.id}
              visit={visit}
              onClick={onVisitClick}
              showNotificationCard={showNotification && index === 0}
            />
          ))}
          {/* New Visit Button after current visits */}
          {showButtonAfter && showNewVisitButton && onNewVisitClick && (
            <div>{newVisitButtonComponent}</div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`${mergedTheme.container} ${className}`}>
      {/* Current Visits with New Visit Button after */}
      {renderSection(currentSectionTitle, data?.current || [], true, true)}

      {/* If no current visits but have past visits, show button before past section */}
      {!hasCurrentVisits &&
        hasPastVisits &&
        showNewVisitButton &&
        onNewVisitClick && (
          <div className="mb-6">{newVisitButtonComponent}</div>
        )}

      {/* Past Visits */}
      {renderSection(pastSectionTitle, data?.past || [])}
    </div>
  );
};
